import { Brand, BrandImportData, BrandSearchResult, BrandImportResult } from '../types/brand';
import { apiRequest, safeAcquireTokenSilent, safeGetAllAccounts } from '../auth/authConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper function to get auth headers
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  try {
    const accounts = await safeGetAllAccounts();
    
    if (accounts.length > 0) {
      const response = await safeAcquireTokenSilent();
      
      if (response.accessToken) {
        return {
          'Authorization': `Bearer ${response.accessToken}`,
          'Content-Type': 'application/json'
        };
      }
    }
    
    return { 'Content-Type': 'application/json' };
  } catch (error) {
    console.error('Error getting auth token:', error);
    return { 'Content-Type': 'application/json' };
  }
};

export const brandService = {
  // Alle Marken laden
  async getAllBrands(): Promise<Brand[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/brands`, { headers });
      if (!response.ok) {
        throw new Error('Failed to fetch brands');
      }
      const result = await response.json();
      // API returns array directly or { data: array }
      return Array.isArray(result) ? result : (result.data || result || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
      return [];
    }
  },

  // Marken suchen
  async searchBrands(query: string): Promise<Brand[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/brands/search?q=${encodeURIComponent(query)}`, { headers });
      if (!response.ok) {
        throw new Error('Failed to search brands');
      }
      const result = await response.json();
      return result.data || result || [];
    } catch (error) {
      console.error('Error searching brands:', error);
      return [];
    }
  },

  // Marken-Details
  async getBrandDetails(value: string): Promise<Brand | null> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/brands/${encodeURIComponent(value)}`, { headers });
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching brand details:', error);
      return null;
    }
  },

  // Marken importieren
  async importBrands(brands: BrandImportData[]): Promise<BrandImportResult> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/brands/import`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ brands })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to import brands: ${response.status} ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error importing brands:', error);
      throw error;
    }
  }
};
